/** @format */

import {ComplexForecastPredicate, ForecastList, ForecastRange, ValueKey} from '../types';

interface Options {
  // Готовый прогноз первого графика.
  forecastA: ForecastList;
  // Готовый прогноз второго графика.
  forecastB: ForecastList;
  // Метод вычисления прогноза.
  predicate: ComplexForecastPredicate;
  // Список дней, для которых нужно вычислить прогноз.
  forecastDays: ValueKey[];
}

/**
 * Вычислит прогноз для производных графиков, зная прогнозы исходных графиков.
 * Подробнее см. https://www.notion.so/1e3b5f12da61808ea75beff52f671469?source=copy_link#1e3b5f12da618090abebeb4dfcfb3e47
 * @param forecastA
 * @param forecastB
 * @param predicate
 * @param forecastDays
 */

const calcComplexForecast = ({
  forecastA,
  forecastB,
  predicate,
  forecastDays,
}: Options): ForecastList => {
  const result: ForecastList = {};

  for (let i = 0; i < forecastDays.length; i++) {
    const key = forecastDays[i];
    const valueA = forecastA[key];
    const valueB = forecastB[key];

    if (valueA && !valueB) {
      // Отсутствует значение Б
      result[key] = {
        average: valueA.average,
        range: [...valueA.range],
      };
      continue;
    }

    if (!valueA && valueB) {
      // Отсутствует значение A
      result[key] = {
        average: valueB.average,
        range: [...valueB.range],
      };
      continue;
    }

    if (!valueA && !valueB) {
      // Отсутствуют оба значения
      continue;
    }

    // Точечный прогноз = предикат от ТОЧЕЧНЫХ прогнозов компонентов (вырожденные диапазоны
    // [avg, avg]) — для ratio-метрик это отношение средних (spentAvg/revAvg для ДРР). Оно совпадает
    // с «метрикой, посчитанной из прогнозов дохода и расхода» и всегда корректного знака.
    const [average] = predicate({
      rangeA: [valueA.average, valueA.average],
      rangeB: [valueB.average, valueB.average],
    });

    // Полоса неопределённости отношения — через распространение погрешности, ЦЕНТРИРОВАННАЯ на точке.
    // Старый cross-диапазон [aMin/bMax, aMax/bMin] для отношения скошен (точка сидит у нижнего края)
    // и взрывается/инвертируется при bMin→0. Берём σ каждого компонента как (верхняя граница −
    // среднее) — floor-устойчиво, т.к. верх не клампят, — и складываем ОТНОСИТЕЛЬНЫЕ погрешности в
    // квадратуре: σ_r / r = √((σa/ā)² + (σb/b̄)²). Полоса = точка × [1 − σ_r/r, 1 + σ_r/r], floor 0.
    const sigmaA = valueA.range[1] - valueA.average;
    const sigmaB = valueB.range[1] - valueB.average;
    const relA = valueA.average !== 0 ? sigmaA / valueA.average : 0;
    const relB = valueB.average !== 0 ? sigmaB / valueB.average : 0;
    const spread = Math.abs(average) * Math.sqrt(relA ** 2 + relB ** 2);
    const range: ForecastRange = [Math.max(0, average - spread), average + spread];

    result[key] = {
      average,
      range,
    };
  }

  return result;
};

export {calcComplexForecast};
